import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { ThemeProvider } from "styled-components";
import LoadingIndicator from "../../components/LoadingIndicator";

import AuthModal from "../AuthModal";
import ContactModal from "../ContactModal";

import WorkspaceLayout from "../../layouts/WorkspaceLayout/loadable";
import PersonalAreaLayout from "../../layouts/PersonalAreaLayout/loadable";
import AdminPanelLayout from "../../layouts/AdminPanelLayout/loadable";
import ThemeLabLayout from "../../layouts/ThemeLabLayout/loadable";
import PublicLayout from "../../layouts/PublicLayout";

import MessageProvider from "./components/MessageProvider";

import {
  makeSelectAuthenticatedUserInfo,
  makeSelectInitialLoadingError,
  makeSelectLoading,
  makeSelectUserDataLoading,
  makeSelectUserInfo,
} from "./selectors";

import TranslationsPage from "../TranslationsPage/loadable";
import QuoteAutofillLab from "../QuoteAutofillLab/loadable";
import AboutPage from "../AboutPage";
import CreateApplicationModal from "../../widgets/CreateApplicationModal";
import CreateLeadModal from "../../widgets/CreateLeadModal";
import { isInternalOffice } from "../../utils/isInternalOffice";
import CreateMessageModal from "../../widgets/CreateMessageModal";
import DealNotificationModal from "../../widgets/DealNotificationModal";
import EditContractorDrawer from "../../widgets/EditContractorDrawer";
import RateHistoryModal from "../../widgets/RateHistoryModal";
import { useAppToken } from "../../theme/v6/useAppToken";
import { getLoginRedirectPath } from "../../auth/routing";
import GlobalStyle from "./styles";
import LoginPage from "../LoginPage/loadable";

const ProtectedRoute = React.memo(({ children }: { children: React.ReactNode }) => {
  const userInfo = useSelector(makeSelectUserInfo());
  const location = useLocation();

  if (!userInfo) {
    return <Navigate to={getLoginRedirectPath(location)} replace />;
  }

  return <>{children}</>;
});

const AdministratorRoute = React.memo(({ children }: { children: React.ReactNode }) => {
  const authenticatedUserInfo = useSelector(makeSelectAuthenticatedUserInfo());
  const location = useLocation();

  if (!authenticatedUserInfo) {
    return <Navigate to={getLoginRedirectPath(location)} replace />;
  }

  if (authenticatedUserInfo.role.userType !== "ADMINISTRATOR") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
});

function App() {
  const loading = useSelector(makeSelectLoading());
  const userDataLoading = useSelector(makeSelectUserDataLoading());
  const error = useSelector(makeSelectInitialLoadingError());

  const userInfo = useSelector(makeSelectUserInfo());

  const { token } = useAppToken();
  const styledTheme = React.useMemo(() => ({ antd6: token }), [token]);

  React.useEffect(() => {
    sessionStorage.setItem("tabId", crypto.randomUUID());
  }, []);

  if (error) {
    return <div>Error....</div>;
  }

  const content =
    loading || userDataLoading ? (
      <LoadingIndicator fullPage />
    ) : (
      <>
        <MessageProvider />
        <AuthModal />
        {userInfo && <CreateApplicationModal />}
        {userInfo && isInternalOffice(userInfo.company.settings.officeType) && <CreateLeadModal />}
        {userInfo && <DealNotificationModal />}
        {userInfo && <RateHistoryModal />}
        {userInfo && <EditContractorDrawer />}
        <CreateMessageModal />
        <ContactModal />
        <Routes>
          <Route
            path="/quote-autofill-lab"
            element={
              <ProtectedRoute>
                <QuoteAutofillLab />
              </ProtectedRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicLayout>
                <LoginPage />
              </PublicLayout>
            }
          />
          <Route
            path="/about"
            element={
              <PublicLayout>
                <AboutPage />
              </PublicLayout>
            }
          />

          <Route
            path="/translations"
            element={
              <ProtectedRoute>
                <TranslationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="personal-area/*"
            element={
              <ProtectedRoute>
                <PersonalAreaLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/*"
            element={
              <ProtectedRoute>
                <AdminPanelLayout />
              </ProtectedRoute>
            }
          />
          <Route
            path="theme-lab/*"
            element={
              <AdministratorRoute>
                <ThemeLabLayout />
              </AdministratorRoute>
            }
          />
          <Route path="/*" element={<WorkspaceLayout />} />
        </Routes>
      </>
    );

  return (
    <React.Suspense fallback={<LoadingIndicator fullPage />}>
      <ThemeProvider theme={styledTheme}>
        <GlobalStyle />
        {content}
      </ThemeProvider>
    </React.Suspense>
  );
}

export default React.memo(App);
